/* eslint-disable max-lines */
import { useMutation, useQueryClient } from 'react-query';
import {
  useApiDelete,
  useApiDownload,
  useApiPatch,
  useApiQuery,
} from 'src/api';
import { useBuildVars } from 'src/config';

export interface DeatilsRequest {
  CUSTOMERSET_ID: number;
  businessType: string | null;
  dealid: number;
  importreferenceid: number;
  isPennyCost?: boolean;
  isPurged?: boolean;
  page: number;
  pageSize: number;
  vpn_prefix?: string;
}
export interface Columns {
  field: string;
  name: string;
  type: string;
}

export interface ColumnGroups {
  columns: Columns[];
  key?: string;
  name: string;
}
export type LineDetails = {
  additional_attributes: {
    attributename: string;
    attributevalue: string;
  }[];
  adjustment_reason: string;
  btl_calc_code: string;
  btl_discount_deal: number;
  btl_effective_date: string;
  btl_expiration_date: string;
  btl_freight_percent: number;
  btl_rebate_goal: number;
  btl_standard_terms: number;
  childDiscountLines?: LineDetails[];
  crCcode: string;
  dd_adjustment_qty: string;
  dd_available_qty: number;
  dd_customer_qty_limit: number | null;
  dd_effective_date: string;
  dd_expiration_date: string;
  dd_max_qty_limit: number;
  dd_min_qty_limit: number;
  dd_percent_of_type: string;
  dd_percent_value: string;
  dd_so_cost: number;
  dd_tracking: number;
  dd_used_qty: number;
  dd_vendor_rem_max_qty_limit: number;
  deal_id: number;
  deal_number: string;
  discount_detail_id: number;
  im_sku: string;
  is_child_line?: boolean;
  is_line_split_and_remove_allowed: boolean;
  is_product_member_exclude: string;
  item_id: number;
  kit_sku: string;
  line_id?: string;
  ntp_deal_type: string;
  ntp_duration: string;
  ntp_magic_key: string;
  ntp_price_protect: string;
  ntp_term_sku_end_date: string;
  ntp_term_sku_start_date: string;
  offeringId: number;
  offeringType: number;
  productId: number;
  product_business_type: string;
  product_group: string;
  productset_type: string;
  red_text1: string;
  red_text2: string;
  red_text3: string;
  red_text4: string;
  sbo2_product_set_Id: number;
  showIncentives: boolean;
  sku: string;
  vd_customer_branch_number: string[];
  vd_eu_segment_code: string;
  vendor_id: number;
  vendor_name: string;
  vendor_number: string;
  vpn: string;
};
export interface DeatilsResponse {
  BcnCameOnDeal: boolean;
  OrderNumber: string;
  actualStatus: string;
  additionalAttributes: {
    key: string;
  };
  autoQuote: string;
  autoquotegenerated: boolean;
  bcn_Linking_Level: string;
  bidstatus: string;
  businessType: string;
  claimname: string;
  claimondate: string;
  cmp_end_user_id: string;
  coG_Approval: string;
  column_groups: ColumnGroups[];
  customersetId: number;
  dealDisplayNumber: string;
  deal_id: number;
  deal_ingested_on: string;
  deal_number: string;
  discounttype: string;
  effective_date: string;
  enableRulesEngine: boolean;
  enable_Custom_OnDemand_Quote: string;
  enable_Quote_QuantityEdit: string;
  end_user_address1: string;
  end_user_address2: string;
  end_user_city: string;
  end_user_contactname: string;
  end_user_country: string;
  end_user_email: string;
  end_user_name: string;
  end_user_number: string;
  end_user_state: string;
  end_user_zipcode: string;
  euaffiliationcount: string;
  expiration_date: string;
  importreferenceid: number;
  isLineIdAllowed?: boolean;
  isSplitAndRemoveAllowed: boolean;
  isXcmVendor: boolean;
  is_resolved_on_exception: boolean;
  isaddressmatchrequired: number;
  lines: LineDetails[];
  lines_page: number;
  lines_page_size: number;
  lines_total_count: number;
  lines_total_pages: number;
  manaualHeaderId: number;
  orderNumber: string;
  orderable: string;
  price_protection_date: string;
  productLocalizationForWeb: string;
  quoteNumber: string;
  reseller_Authorization: string;
  reseller_name: string;
  reseller_number: string;
  sbo_currencycode: string;
  showIncentivesHeader: boolean;
  status: string;
  statusid: number;
  treeView_lines_total_count: number;
  vendor_discount_type: string;
  vendor_id: number;
  vendor_name: string;
  vendor_program_id: number;
  vendor_program_name: string;
  version: number;
}

export type DiscountLinesLookup = {
  vpn: string;
};

export function useDealDetailsQuery(
  { importreferenceid, vpn_prefix, ...params }: DeatilsRequest,
  opts = {}
) {
  const reqBody = vpn_prefix ? { vpn_prefix, ...params } : params;
  const buildVars = useBuildVars();
  return useApiQuery<DeatilsResponse>(
    `/system.webapi.gds/api/v1/GdsCoreApiDeals/${importreferenceid}`,
    reqBody,
    opts,
    undefined,
    {
      host: buildVars.VP_API_BASE,
    }
  );
}

export interface DealDetailsFile {
  countryCode: string;
  importreferenceid: number;
}

export function useDealDetailsDownloadFile(params: DealDetailsFile) {
  const buildVars = useBuildVars();
  const apiGetDownload = useApiDownload({ host: buildVars.VP_API_BASE });
  return useMutation(() =>
    apiGetDownload(
      `/system.webapi.gds/api/v1/GdsCoreApiDeals/${params.importreferenceid}.xlsx`,
      params
    )
  );
}

export type ChangeExpirationRequest = {
  applyToAllLines: boolean;
  countryCode: string;
  dealid: number;
  effective_date: string | null;
  expiration_date: string | null;
  importreferenceid: number;
  page: number;
  pageSize: number;
};

export function useExpirationDateChange(params: ChangeExpirationRequest) {
  const queryClient = useQueryClient();
  const buildVars = useBuildVars();
  const apiPatch = useApiPatch(buildVars.VP_API_BASE);
  return useMutation(
    () =>
      apiPatch(
        `/system.webapi.gds/api/v1/GdsCoreApiDeals/${params.importreferenceid}`,
        {
          applyToAllLines: params.applyToAllLines,
          effective_date: params.effective_date,
          expiration_date: params.expiration_date,
        },
        {
          countryCode: params.countryCode,
          dealid: params.dealid,
          page: params.page,
          pageSize: params.pageSize,
        }
      ),
    {
      onSuccess: (data) => {
        queryClient
          .invalidateQueries(
            `/system.webapi.gds/api/v1/GdsCoreApiDeals/${params.importreferenceid}`
          )
          .catch(() => undefined);
      },
    }
  );
}

export function useDealsDeactivate(
  importreferenceid: number,
  params: {
    dealnumber: string;
    dealversion: number;
    editor: string;
  }
) {
  const buildVars = useBuildVars();
  const apiDelete = useApiDelete(buildVars.VP_API_BASE);

  return useMutation((deactivatereason: string) => {
    return apiDelete(
      `/system.webapi.gds/api/v1/GdsCoreApiDeals/${importreferenceid}`,
      undefined,
      { ...params, deactivatereason }
    );
  });
}

export function useDiscountLinesLookup(
  importreferenceid: number,
  params: {
    dealid: number;
    page: number;
    pageSize: number;
    q: string;
  }
) {
  const buildVars = useBuildVars();
  return useApiQuery<DiscountLinesLookup[]>(
    `/system.webapi.gds/api/v1/GdsCoreApiDeals/${importreferenceid}/discountLineVPNs`,
    params,
    { enabled: params.q.length > 1 },
    undefined,
    {
      host: buildVars.VP_API_BASE,
    }
  );
}

export function useDiscountLinesFilter(
  { importreferenceid, ...params }: DeatilsRequest & { q: string },
  opts = {}
) {
  const buildVars = useBuildVars();
  return useApiQuery<DeatilsResponse>(
    `/system.webapi.gds/api/v1/GdsCoreApiDeals/${importreferenceid}/filtered`,
    params,
    { enabled: params.q.length > 1 },
    undefined,
    {
      host: buildVars.VP_API_BASE,
    }
  );
}

export interface ResellerSearchRequest {
  reseller_name: string;
  reseller_no: string;
}
export interface ResellerSearchResponse {
  reseller_name: string;
  reseller_no: string;
}

export function useResellerSearch(params: ResellerSearchRequest) {
  const buildVars = useBuildVars();
  return useApiQuery<ResellerSearchResponse[]>(
    '/system.webapi.gds/api/v1/GdsCoreApiDeals/resellers',
    params,
    {
      enabled: params.reseller_name.length > 1 || params.reseller_no.length > 1,
    },
    undefined,
    {
      host: buildVars.VP_API_BASE,
    }
  );
}

export interface EnduserAffiliationList {
  address: string;
  bidId: number;
  city: string;
  country: string;
  endUserAffiliationId: number;
  endUserID: number;
  endUserName: string;
  postalCode: string;
  state: string;
}
export interface EnduserAffiliationResponse {
  asyncState: string;
  creationOptions: string;
  exception: string;
  id: number;
  isCanceled: boolean;
  isCompleted: boolean;
  isCompletedSuccessfully: boolean;
  isFaulted: boolean;
  result: EnduserAffiliationList[];
  status: number;
}

export function useEnduserAffiliationListQuery(
  params: { bidId: string },
  enabled: boolean
) {
  const buildVars = useBuildVars();
  return useApiQuery<EnduserAffiliationResponse>(
    '/system.webapi.gds/api/v1/GdsCoreApiEnduser/EnduserAffiliationlist',
    params,
    { enabled: enabled },
    undefined,
    {
      host: buildVars.VP_API_BASE,
    }
  );
}

export function useEnduserListQuery(
  params: { bidId: string },
  enabled: boolean
) {
  const buildVars = useBuildVars();
  return useApiQuery<
    {
      cmpEnduserId: string;
      endUserName: string;
      endUserNumber: string;
      modifiedBy: string;
      modifiedOn: string;
    }[]
  >(
    '/system.webapi.gds/api/v1/GdsCoreApiEnduser/EndUsersList',
    params,
    { enabled: enabled },
    undefined,
    {
      host: buildVars.VP_API_BASE,
    }
  );
}

export const useGetHeaderText = (line: LineDetails): string => {
  if (line.vpn !== '' && line.vpn !== null) return line.vpn;
  else if (line.vendor_number !== '' && line.vendor_number !== null)
    return line.vendor_number;
  else return line.product_group;
};

export type UpdateCliamItRequest = {
  claimname: string | null;
  claimondate: string | null;
  dealid: number;
  importreferenceid: number;
};

export function useUpdateCliamIt() {
  const buildVars = useBuildVars();
  const queryClient = useQueryClient();
  const apiPatch = useApiPatch(buildVars.VP_API_BASE);

  return useMutation(
    (request: UpdateCliamItRequest) => {
      return apiPatch(
        `/system.webapi.gds/api/v1/GdsCoreApiDeals/updateclaiminfo`,
        request
      );
    },
    {
      onSuccess: (_, request) => {
        queryClient.invalidateQueries(
          `/system.webapi.gds/api/v1/GdsCoreApiDeals/${String(
            request.dealid
          )}/deal-header-attributes`
        );
      },
    }
  );
}
export function useGetDealHeaderAttributes(params: { dealId?: number }) {
  const buildVars = useBuildVars();
  return useApiQuery<
    {
      attributename: string;
      attributevalue: string;
    }[]
  >(
    `/system.webapi.gds/api/v1/GdsCoreApiDeals/${params.dealId}/deal-header-attributes`,
    undefined,
    undefined,
    undefined,
    {
      host: buildVars.VP_API_BASE,
    }
  );
}
